// Comprehensive country data for individual country pages
// Inspired by TecEx's country detail pages

export interface CountryMetrics {
  taxRate: string;
  dutyRate: string;
  leadTime: string;
  currency: string;
  language: string[];
  timezone: string;
}

export interface ComplianceInfo {
  title: string;
  description: string;
}

export interface CountryData {
  name: string;
  code: string; // ISO 2-letter code
  slug: string; // URL-friendly name
  capital: string;
  population: string;
  region: string;
  tagline: string;
  description: string;
  metrics: CountryMetrics;
  keyIndustries: string[];
  prohibitedItems: string[];
  restrictedItems: string[];
  compliance: ComplianceInfo[];
  tradeAgreements: string[];
  painPoints: string[];
  services: string[];
  hasOffice: boolean;
  isPartner: boolean;
  officeLocation?: string;
}

// Comprehensive country database
export const COUNTRY_DATA: { [code: string]: CountryData } = {
  // === ASIA ===
  SG: {
    name: 'Singapore',
    code: 'SG',
    slug: 'singapore',
    capital: 'Singapore',
    population: '5.9 million',
    region: 'Asia',
    tagline: 'Gateway to Southeast Asia with a world-class logistics infrastructure',
    description: 'Singapore is a global trading hub with one of the world\'s most efficient customs systems. Its strategic location, pro-business environment, and advanced digital infrastructure make it an ideal base for regional operations.',
    metrics: {
      taxRate: 'Up to 9% GST',
      dutyRate: '0% (Free Trade Zone)',
      leadTime: '2-3 business days',
      currency: 'SGD (Singapore Dollar)',
      language: ['English', 'Mandarin', 'Malay', 'Tamil'],
      timezone: 'SGT (UTC+8)',
    },
    keyIndustries: ['Electronics', 'Pharmaceuticals', 'Petrochemicals', 'Financial Services', 'Technology'],
    prohibitedItems: ['Chewing gum (without permit)', 'Firecrackers', 'Endangered wildlife products', 'Controlled drugs'],
    restrictedItems: ['Telecommunications equipment', 'Pharmaceuticals', 'Food products', 'Cosmetics', 'Dual-use goods'],
    compliance: [
      {
        title: 'IMDA Approval',
        description: 'Telecommunications and broadcasting equipment requires approval from the Infocomm Media Development Authority.',
      },
      {
        title: 'NEA Approval',
        description: 'Secondhand electrical and electronic products require National Environment Agency approval.',
      },
      {
        title: 'UEN Registration',
        description: 'Companies must have a Unique Entity Number (UEN) for customs declarations.',
      },
      {
        title: 'Strategic Goods Control',
        description: 'Military and dual-use goods require strategic goods permits under Singapore\'s export control regime.',
      },
    ],
    tradeAgreements: ['RCEP', 'CPTPP', 'ASEAN FTA', 'Singapore-US FTA', 'Singapore-EU FTA', 'Singapore-China FTA'],
    painPoints: [
      'Complex permit requirements for controlled goods',
      'GST payment timing requirements',
      'Strict documentation standards',
      'Dual-use technology compliance',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'Warehousing', 'Last-Mile Delivery', 'Trade Compliance'],
    hasOffice: true,
    isPartner: false,
    officeLocation: 'Truelog Headquarters',
  },
  IN: {
    name: 'India',
    code: 'IN',
    slug: 'india',
    capital: 'New Delhi',
    population: '1.4 billion',
    region: 'Asia',
    tagline: 'A rapidly growing market with vast opportunities and complex regulations',
    description: 'India presents enormous opportunities with its massive consumer market and growing manufacturing sector. However, navigating its complex customs procedures, multiple tax jurisdictions, and regulatory requirements demands local expertise.',
    metrics: {
      taxRate: 'Up to 28% GST + IGST',
      dutyRate: '5-100% (varies by product)',
      leadTime: '5-10 business days',
      currency: 'INR (Indian Rupee)',
      language: ['Hindi', 'English'],
      timezone: 'IST (UTC+5:30)',
    },
    keyIndustries: ['IT Services', 'Pharmaceuticals', 'Textiles', 'Automotive', 'Agriculture'],
    prohibitedItems: ['Ivory and wildlife products', 'Beef products', 'Maps depicting incorrect borders', 'Narcotic drugs'],
    restrictedItems: ['Electronics', 'Pharmaceuticals', 'Food products', 'Chemicals', 'Cosmetics'],
    compliance: [
      {
        title: 'BIS Certification',
        description: 'Many electronic products require Bureau of Indian Standards certification before import.',
      },
      {
        title: 'CDSCO Approval',
        description: 'Pharmaceuticals and medical devices need Central Drugs Standard Control Organisation approval.',
      },
      {
        title: 'FSSAI License',
        description: 'Food products require Food Safety and Standards Authority of India registration.',
      },
      {
        title: 'Import Export Code',
        description: 'All importers and exporters must have an IEC from the Directorate General of Foreign Trade.',
      },
    ],
    tradeAgreements: ['RCEP (not signed)', 'India-ASEAN FTA', 'India-Japan CEPA', 'India-UAE CEPA', 'India-Australia ECTA'],
    painPoints: [
      'Complex GST structure with multiple rates',
      'Port congestion and clearance delays',
      'Frequent regulatory changes',
      'State-level compliance requirements',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'BIS Certification Support', 'Warehousing', 'Distribution'],
    hasOffice: true,
    isPartner: false,
    officeLocation: 'Bangalore Office',
  },
  VN: {
    name: 'Vietnam',
    code: 'VN',
    slug: 'vietnam',
    capital: 'Hanoi',
    population: '100 million',
    region: 'Asia',
    tagline: 'Emerging manufacturing hub with competitive advantages',
    description: 'Vietnam has emerged as a key manufacturing destination, attracting businesses seeking to diversify supply chains. With competitive labor costs and numerous trade agreements, it offers significant advantages for import and export operations.',
    metrics: {
      taxRate: '10% VAT',
      dutyRate: '0-50% (varies)',
      leadTime: '3-5 business days',
      currency: 'VND (Vietnamese Dong)',
      language: ['Vietnamese'],
      timezone: 'ICT (UTC+7)',
    },
    keyIndustries: ['Electronics', 'Textiles & Apparel', 'Footwear', 'Furniture', 'Seafood'],
    prohibitedItems: ['Weapons', 'Narcotic drugs', 'Toxic chemicals', 'Pornographic materials'],
    restrictedItems: ['Pharmaceuticals', 'Cosmetics', 'Food products', 'Chemicals', 'Secondhand goods'],
    compliance: [
      {
        title: 'Product Registration',
        description: 'Many products require registration with relevant ministries before import.',
      },
      {
        title: 'C/O Requirements',
        description: 'Certificate of Origin is crucial for FTA preferential rates.',
      },
      {
        title: 'Quality Certification',
        description: 'Certain products require Vietnam quality certification (CR marking).',
      },
    ],
    tradeAgreements: ['RCEP', 'CPTPP', 'EVFTA', 'ASEAN FTA', 'Vietnam-Korea FTA', 'Vietnam-UK FTA'],
    painPoints: [
      'Language barriers in documentation',
      'Varying interpretation of regulations',
      'Infrastructure limitations in some areas',
      'Foreign ownership restrictions',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'Warehousing', 'Cross-Border Solutions', 'Distribution'],
    hasOffice: true,
    isPartner: false,
    officeLocation: 'Ho Chi Minh City Office',
  },
  JP: {
    name: 'Japan',
    code: 'JP',
    slug: 'japan',
    capital: 'Tokyo',
    population: '125 million',
    region: 'Asia',
    tagline: 'Advanced market with high standards and sophisticated logistics',
    description: 'Japan is a technologically advanced market with stringent quality standards and sophisticated consumer expectations. Success requires understanding local business culture and meeting precise regulatory requirements.',
    metrics: {
      taxRate: '10% Consumption Tax',
      dutyRate: '0-30% (varies)',
      leadTime: '3-5 business days',
      currency: 'JPY (Japanese Yen)',
      language: ['Japanese'],
      timezone: 'JST (UTC+9)',
    },
    keyIndustries: ['Automotive', 'Electronics', 'Robotics', 'Pharmaceuticals', 'Machinery'],
    prohibitedItems: ['Narcotics', 'Firearms', 'Counterfeit goods', 'Certain plants and animals'],
    restrictedItems: ['Food products', 'Cosmetics', 'Medical devices', 'Chemicals', 'Pharmaceuticals'],
    compliance: [
      {
        title: 'PSE Mark',
        description: 'Electrical products require Product Safety of Electrical Appliance & Materials certification.',
      },
      {
        title: 'Food Sanitation Act',
        description: 'Food products must comply with Japan\'s strict food safety regulations.',
      },
      {
        title: 'PMDA Approval',
        description: 'Pharmaceuticals and medical devices require Pharmaceuticals and Medical Devices Agency approval.',
      },
    ],
    tradeAgreements: ['RCEP', 'CPTPP', 'Japan-EU EPA', 'Japan-US Trade Agreement', 'Japan-UK CEPA'],
    painPoints: [
      'Strict labeling requirements in Japanese',
      'High quality expectations',
      'Complex distribution system (keiretsu)',
      'Cultural business practices',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'Regulatory Compliance', 'Distribution', 'Returns Management'],
    hasOffice: false,
    isPartner: true,
  },
  CN: {
    name: 'China',
    code: 'CN',
    slug: 'china',
    capital: 'Beijing',
    population: '1.4 billion',
    region: 'Asia',
    tagline: 'The world\'s factory and a massive consumer market',
    description: 'China remains the world\'s largest manufacturing hub and a critical market for global trade. Navigating its regulatory environment requires deep local knowledge and established relationships with authorities.',
    metrics: {
      taxRate: '13% VAT (standard)',
      dutyRate: '0-65% (varies)',
      leadTime: '5-10 business days',
      currency: 'CNY (Chinese Yuan)',
      language: ['Mandarin Chinese'],
      timezone: 'CST (UTC+8)',
    },
    keyIndustries: ['Electronics', 'Machinery', 'Textiles', 'Chemicals', 'Automotive'],
    prohibitedItems: ['Weapons', 'Narcotics', 'Politically sensitive materials', 'Certain animal products'],
    restrictedItems: ['Pharmaceuticals', 'Cosmetics', 'Medical devices', 'Food products', 'Chemicals'],
    compliance: [
      {
        title: 'CCC Certification',
        description: 'China Compulsory Certification required for many products before import.',
      },
      {
        title: 'NMPA Registration',
        description: 'Cosmetics, pharmaceuticals, and medical devices require National Medical Products Administration approval.',
      },
      {
        title: 'GACC Registration',
        description: 'Food importers must register with the General Administration of Customs.',
      },
    ],
    tradeAgreements: ['RCEP', 'China-ASEAN FTA', 'China-Australia FTA', 'China-Korea FTA', 'China-Switzerland FTA'],
    painPoints: [
      'Complex regulatory landscape',
      'Cybersecurity and data localization requirements',
      'Intellectual property concerns',
      'Changing trade policies',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'CCC Certification Support', 'Warehousing', 'Cross-Border E-commerce'],
    hasOffice: false,
    isPartner: true,
  },
  MY: {
    name: 'Malaysia',
    code: 'MY',
    slug: 'malaysia',
    capital: 'Kuala Lumpur',
    population: '33 million',
    region: 'Asia',
    tagline: 'Strategic ASEAN hub with diverse economic opportunities',
    description: 'Malaysia offers a strategic location in ASEAN with well-developed infrastructure, business-friendly policies, and a diverse economy spanning electronics, palm oil, and Islamic finance.',
    metrics: {
      taxRate: '6% SST (Sales & Service Tax)',
      dutyRate: '0-60% (varies)',
      leadTime: '3-5 business days',
      currency: 'MYR (Malaysian Ringgit)',
      language: ['Malay', 'English', 'Mandarin', 'Tamil'],
      timezone: 'MYT (UTC+8)',
    },
    keyIndustries: ['Electronics', 'Palm Oil', 'Petroleum', 'Automotive', 'Islamic Finance'],
    prohibitedItems: ['Narcotics', 'Weapons', 'Obscene materials', 'Counterfeit currency'],
    restrictedItems: ['Pharmaceuticals', 'Cosmetics', 'Food products', 'Electronics', 'Halal products'],
    compliance: [
      {
        title: 'SIRIM Certification',
        description: 'Many products require certification from the Standards and Industrial Research Institute of Malaysia.',
      },
      {
        title: 'Halal Certification',
        description: 'Food products may require JAKIM halal certification for Muslim consumers.',
      },
      {
        title: 'Approved Permit',
        description: 'Certain products require Approved Permits (APs) from relevant ministries.',
      },
    ],
    tradeAgreements: ['RCEP', 'CPTPP', 'ASEAN FTA', 'Malaysia-Australia FTA', 'Malaysia-Japan EPA'],
    painPoints: [
      'Halal compliance requirements',
      'AP system for controlled items',
      'Multi-ethnic market considerations',
      'Documentation in Malay sometimes required',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'Halal Logistics', 'Warehousing', 'Distribution'],
    hasOffice: false,
    isPartner: true,
  },
  TH: {
    name: 'Thailand',
    code: 'TH',
    slug: 'thailand',
    capital: 'Bangkok',
    population: '70 million',
    region: 'Asia',
    tagline: 'Key manufacturing base in Southeast Asia',
    description: 'Thailand is a major manufacturing and export base in ASEAN, known for automotive, electronics, and food processing industries. Its central location makes it an ideal regional distribution hub.',
    metrics: {
      taxRate: '7% VAT',
      dutyRate: '0-80% (varies)',
      leadTime: '3-5 business days',
      currency: 'THB (Thai Baht)',
      language: ['Thai'],
      timezone: 'ICT (UTC+7)',
    },
    keyIndustries: ['Automotive', 'Electronics', 'Food Processing', 'Tourism', 'Petrochemicals'],
    prohibitedItems: ['Narcotics', 'Pornography', 'E-cigarettes', 'Counterfeit goods'],
    restrictedItems: ['Pharmaceuticals', 'Cosmetics', 'Food products', 'Medical devices', 'Chemicals'],
    compliance: [
      {
        title: 'TISI Standards',
        description: 'Thai Industrial Standards Institute certification required for regulated products.',
      },
      {
        title: 'FDA Thailand',
        description: 'Food, drugs, cosmetics, and medical devices require FDA Thailand registration.',
      },
      {
        title: 'NBTC Approval',
        description: 'Telecommunications equipment needs approval from the National Broadcasting and Telecommunications Commission.',
      },
    ],
    tradeAgreements: ['RCEP', 'ASEAN FTA', 'Thailand-Australia FTA', 'JTEPA', 'TAFTA'],
    painPoints: [
      'Thai language documentation requirements',
      'Complex licensing for certain products',
      'Customs valuation disputes',
      'Port congestion during peak seasons',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'Warehousing', 'Distribution', 'Trade Compliance'],
    hasOffice: false,
    isPartner: true,
  },
  ID: {
    name: 'Indonesia',
    code: 'ID',
    slug: 'indonesia',
    capital: 'Jakarta',
    population: '275 million',
    region: 'Asia',
    tagline: 'ASEAN\'s largest economy with immense potential',
    description: 'Indonesia is ASEAN\'s largest economy with a young, growing population. The archipelago presents unique logistics challenges but offers tremendous opportunities for businesses willing to navigate its regulatory environment.',
    metrics: {
      taxRate: '11% VAT',
      dutyRate: '0-150% (varies)',
      leadTime: '5-10 business days',
      currency: 'IDR (Indonesian Rupiah)',
      language: ['Indonesian (Bahasa)'],
      timezone: 'WIB (UTC+7)',
    },
    keyIndustries: ['Palm Oil', 'Mining', 'Automotive', 'Textiles', 'Food & Beverage'],
    prohibitedItems: ['Narcotics', 'Weapons', 'Gambling equipment', 'Pornographic materials'],
    restrictedItems: ['Pharmaceuticals', 'Food products', 'Cosmetics', 'Electronics', 'Textiles'],
    compliance: [
      {
        title: 'SNI Certification',
        description: 'Indonesian National Standard (SNI) certification required for many products.',
      },
      {
        title: 'BPOM Registration',
        description: 'Food, drugs, cosmetics, and medical devices require BPOM registration.',
      },
      {
        title: 'API Licensing',
        description: 'Importers need API (Angka Pengenal Impor) license to import goods.',
      },
      {
        title: 'Halal Certification',
        description: 'Certain products require mandatory halal certification.',
      },
    ],
    tradeAgreements: ['RCEP', 'ASEAN FTA', 'Indonesia-Australia CEPA', 'Indonesia-Japan EPA', 'Indonesia-Pakistan PTA'],
    painPoints: [
      'Complex licensing requirements (SNI, BPOM, API)',
      'Pre-shipment inspection for certain goods',
      'Archipelago logistics challenges',
      'Changing import regulations',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'Regulatory Compliance', 'Warehousing', 'Island Distribution'],
    hasOffice: false,
    isPartner: true,
  },
  PH: {
    name: 'Philippines',
    code: 'PH',
    slug: 'philippines',
    capital: 'Manila',
    population: '115 million',
    region: 'Asia',
    tagline: 'English-speaking gateway to ASEAN',
    description: 'The Philippines offers a young, English-speaking workforce and a rapidly growing consumer market. Its BPO industry is world-renowned, and the country is emerging as a manufacturing alternative.',
    metrics: {
      taxRate: '12% VAT',
      dutyRate: '0-65% (varies)',
      leadTime: '3-7 business days',
      currency: 'PHP (Philippine Peso)',
      language: ['Filipino', 'English'],
      timezone: 'PHT (UTC+8)',
    },
    keyIndustries: ['BPO/IT Services', 'Electronics', 'Semiconductors', 'Food Processing', 'Shipbuilding'],
    prohibitedItems: ['Narcotics', 'Firearms', 'Gambling devices', 'Subversive materials'],
    restrictedItems: ['Pharmaceuticals', 'Food products', 'Chemicals', 'Electronics', 'Medical devices'],
    compliance: [
      {
        title: 'FDA Philippines',
        description: 'Food, drugs, cosmetics, and medical devices require FDA registration.',
      },
      {
        title: 'BPS Certification',
        description: 'Bureau of Philippine Standards certification for regulated products.',
      },
      {
        title: 'NTC Type Approval',
        description: 'Telecommunications equipment requires National Telecommunications Commission approval.',
      },
    ],
    tradeAgreements: ['RCEP', 'ASEAN FTA', 'PH-EFTA FTA', 'PH-Japan EPA', 'Generalized System of Preferences'],
    painPoints: [
      'Customs clearance delays',
      'Port congestion in Manila',
      'Complex agency coordination',
      'Inter-island logistics',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'Warehousing', 'Distribution', 'Last-Mile Delivery'],
    hasOffice: false,
    isPartner: true,
  },
  KR: {
    name: 'South Korea',
    code: 'KR',
    slug: 'south-korea',
    capital: 'Seoul',
    population: '52 million',
    region: 'Asia',
    tagline: 'Tech-driven economy with sophisticated consumers',
    description: 'South Korea is a highly developed economy known for technology, automotive, and K-culture. Its tech-savvy consumers and advanced infrastructure make it an attractive but competitive market.',
    metrics: {
      taxRate: '10% VAT',
      dutyRate: '0-40% (varies)',
      leadTime: '3-5 business days',
      currency: 'KRW (Korean Won)',
      language: ['Korean'],
      timezone: 'KST (UTC+9)',
    },
    keyIndustries: ['Electronics', 'Automotive', 'Shipbuilding', 'Petrochemicals', 'Cosmetics'],
    prohibitedItems: ['Narcotics', 'Counterfeit goods', 'Materials undermining public order', 'Endangered species'],
    restrictedItems: ['Pharmaceuticals', 'Food products', 'Cosmetics', 'Medical devices', 'Telecommunications equipment'],
    compliance: [
      {
        title: 'KC Mark',
        description: 'Korea Certification mark required for electrical and electronic products.',
      },
      {
        title: 'MFDS Approval',
        description: 'Ministry of Food and Drug Safety approval for food, drugs, cosmetics, and medical devices.',
      },
      {
        title: 'KCS Procedures',
        description: 'Korea Customs Service has specific documentation and valuation requirements.',
      },
    ],
    tradeAgreements: ['RCEP', 'Korea-US FTA', 'Korea-EU FTA', 'Korea-China FTA', 'ASEAN-Korea FTA'],
    painPoints: [
      'Korean language requirements',
      'Strict KC marking compliance',
      'High quality expectations',
      'Competitive local market',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'KC Certification Support', 'Warehousing', 'E-commerce Fulfillment'],
    hasOffice: false,
    isPartner: true,
  },
  HK: {
    name: 'Hong Kong',
    code: 'HK',
    slug: 'hong-kong',
    capital: 'Hong Kong',
    population: '7.5 million',
    region: 'Asia',
    tagline: 'Free port with seamless trade facilitation',
    description: 'Hong Kong is a free port with minimal customs duties and a highly efficient trade facilitation system. It serves as a major transshipment hub and gateway to mainland China.',
    metrics: {
      taxRate: '0% (No VAT/GST)',
      dutyRate: '0% (Free Port)',
      leadTime: '1-2 business days',
      currency: 'HKD (Hong Kong Dollar)',
      language: ['Cantonese', 'English', 'Mandarin'],
      timezone: 'HKT (UTC+8)',
    },
    keyIndustries: ['Financial Services', 'Trading', 'Logistics', 'Professional Services', 'Tourism'],
    prohibitedItems: ['Narcotics', 'Firearms', 'Endangered species', 'Counterfeit goods'],
    restrictedItems: ['Pharmaceuticals', 'Telecommunications equipment', 'Strategic commodities', 'Rice'],
    compliance: [
      {
        title: 'TID License',
        description: 'Trade and Industry Department licenses for certain controlled goods.',
      },
      {
        title: 'DH Registration',
        description: 'Department of Health registration for pharmaceuticals.',
      },
      {
        title: 'Strategic Commodities Control',
        description: 'License required for strategic commodities and dual-use goods.',
      },
    ],
    tradeAgreements: ['WTO Member', 'Hong Kong-ASEAN FTA', 'CEPA (Mainland China)', 'Hong Kong-Chile FTA'],
    painPoints: [
      'Limited warehouse space',
      'High operating costs',
      'Strategic commodities compliance',
      'China-related geopolitical considerations',
    ],
    services: ['IOR Services', 'Transshipment', 'Warehousing', 'China Gateway Services', 'Trade Finance'],
    hasOffice: false,
    isPartner: true,
  },
  IL: {
    name: 'Israel',
    code: 'IL',
    slug: 'israel',
    capital: 'Jerusalem',
    population: '9.5 million',
    region: 'Asia',
    tagline: 'Innovation nation with advanced technology sector',
    description: 'Israel is known as the "Startup Nation" with a thriving technology and innovation sector. Despite its small size, it\'s a significant market for high-tech products and services.',
    metrics: {
      taxRate: '17% VAT',
      dutyRate: '0-12% (varies)',
      leadTime: '5-7 business days',
      currency: 'ILS (Israeli Shekel)',
      language: ['Hebrew', 'Arabic', 'English'],
      timezone: 'IST (UTC+2)',
    },
    keyIndustries: ['High-Tech', 'Cybersecurity', 'Pharmaceuticals', 'Defense', 'Agriculture'],
    prohibitedItems: ['Narcotics', 'Weapons (without permit)', 'Boycott-related materials', 'Counterfeit goods'],
    restrictedItems: ['Food products', 'Pharmaceuticals', 'Cosmetics', 'Telecommunications equipment', 'Defense-related items'],
    compliance: [
      {
        title: 'SII Standards',
        description: 'Standards Institution of Israel certification for regulated products.',
      },
      {
        title: 'MOH Registration',
        description: 'Ministry of Health registration for pharmaceuticals and medical devices.',
      },
      {
        title: 'Kosher Certification',
        description: 'Many food products benefit from Kosher certification.',
      },
    ],
    tradeAgreements: ['Israel-EU Association Agreement', 'Israel-US FTA', 'Israel-UK FTA', 'Israel-Mercosur FTA'],
    painPoints: [
      'Security-related shipping delays',
      'Kosher requirements for food',
      'SII certification process',
      'Regional logistics considerations',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'Security Compliant Shipping', 'Warehousing', 'Distribution'],
    hasOffice: false,
    isPartner: true,
  },
  AE: {
    name: 'United Arab Emirates',
    code: 'AE',
    slug: 'uae',
    capital: 'Abu Dhabi',
    population: '10 million',
    region: 'Asia',
    tagline: 'Regional trading hub bridging East and West',
    description: 'The UAE, particularly Dubai, serves as a major regional trading hub connecting Asia, Europe, and Africa. Its free zones, world-class infrastructure, and business-friendly environment attract global businesses.',
    metrics: {
      taxRate: '5% VAT',
      dutyRate: '5% (GCC Common External Tariff)',
      leadTime: '3-5 business days',
      currency: 'AED (UAE Dirham)',
      language: ['Arabic', 'English'],
      timezone: 'GST (UTC+4)',
    },
    keyIndustries: ['Oil & Gas', 'Trading', 'Tourism', 'Financial Services', 'Aviation'],
    prohibitedItems: ['Narcotics', 'Pork products', 'Gambling equipment', 'Pornographic materials'],
    restrictedItems: ['Food products', 'Pharmaceuticals', 'Cosmetics', 'Electronics', 'Alcohol'],
    compliance: [
      {
        title: 'Emirates Authority',
        description: 'Various Emirates authorities regulate product standards and safety.',
      },
      {
        title: 'MOH Registration',
        description: 'Ministry of Health registration for pharmaceuticals and medical devices.',
      },
      {
        title: 'Halal Certification',
        description: 'Food products must comply with UAE halal requirements.',
      },
    ],
    tradeAgreements: ['GCC Customs Union', 'UAE-India CEPA', 'UAE-Israel FTA', 'UAE-Indonesia CEPA'],
    painPoints: [
      'Halal compliance requirements',
      'Product registration processes',
      'Multiple authority coordination',
      'Free zone vs mainland regulations',
    ],
    services: ['IOR Services', 'Free Zone Solutions', 'Customs Brokerage', 'Warehousing', 'Regional Distribution'],
    hasOffice: false,
    isPartner: true,
  },

  // === EUROPE ===
  BE: {
    name: 'Belgium',
    code: 'BE',
    slug: 'belgium',
    capital: 'Brussels',
    population: '11.5 million',
    region: 'Europe',
    tagline: 'Heart of Europe and gateway to the EU market',
    description: 'Belgium, home to EU headquarters and major ports like Antwerp, is a strategic logistics hub for accessing the European market. Its central location and multilingual workforce make it ideal for pan-European operations.',
    metrics: {
      taxRate: '21% VAT',
      dutyRate: 'EU Common Customs Tariff',
      leadTime: '2-4 business days',
      currency: 'EUR (Euro)',
      language: ['Dutch', 'French', 'German'],
      timezone: 'CET (UTC+1)',
    },
    keyIndustries: ['Pharmaceuticals', 'Chemicals', 'Logistics', 'Automotive', 'Food Processing'],
    prohibitedItems: ['Narcotics', 'Counterfeit goods', 'Weapons (without permit)', 'Certain animal products'],
    restrictedItems: ['Pharmaceuticals', 'Food products', 'Chemicals', 'Medical devices', 'Cosmetics'],
    compliance: [
      {
        title: 'CE Marking',
        description: 'Products sold in the EU must comply with CE marking requirements.',
      },
      {
        title: 'REACH Compliance',
        description: 'Chemical products must comply with EU REACH regulations.',
      },
      {
        title: 'EUDAMED',
        description: 'Medical devices must be registered in the European Database on Medical Devices.',
      },
    ],
    tradeAgreements: ['EU Single Market', 'EU-UK TCA', 'EU-Canada CETA', 'EU-Japan EPA', 'EU-Singapore FTA'],
    painPoints: [
      'Complex VAT rules for cross-border B2B',
      'Multiple language requirements',
      'REACH compliance for chemicals',
      'Product safety regulations',
    ],
    services: ['IOR Services', 'EU Customs Brokerage', 'Warehousing', 'Pan-European Distribution', 'VAT Services'],
    hasOffice: true,
    isPartner: false,
    officeLocation: 'Antwerp Office',
  },
  DE: {
    name: 'Germany',
    code: 'DE',
    slug: 'germany',
    capital: 'Berlin',
    population: '84 million',
    region: 'Europe',
    tagline: 'Europe\'s largest economy and manufacturing powerhouse',
    description: 'Germany is Europe\'s largest economy and a global leader in automotive, machinery, and chemicals. Its demanding consumers and strict regulations require high-quality products and compliance.',
    metrics: {
      taxRate: '19% VAT',
      dutyRate: 'EU Common Customs Tariff',
      leadTime: '2-4 business days',
      currency: 'EUR (Euro)',
      language: ['German'],
      timezone: 'CET (UTC+1)',
    },
    keyIndustries: ['Automotive', 'Machinery', 'Chemicals', 'Electronics', 'Pharmaceuticals'],
    prohibitedItems: ['Nazi memorabilia', 'Narcotics', 'Counterfeit goods', 'Certain weapons'],
    restrictedItems: ['Pharmaceuticals', 'Food products', 'Electronics', 'Chemicals', 'Medical devices'],
    compliance: [
      {
        title: 'CE Marking',
        description: 'Mandatory CE marking for regulated product categories.',
      },
      {
        title: 'German Packaging Act',
        description: 'Packaging must be registered with LUCID and comply with EPR requirements.',
      },
      {
        title: 'ElektroG',
        description: 'Electronics must be registered with the EAR foundation.',
      },
    ],
    tradeAgreements: ['EU Single Market', 'EU-UK TCA', 'EU-Canada CETA', 'EU-Japan EPA', 'EU-Korea FTA'],
    painPoints: [
      'German language requirements',
      'Strict packaging regulations',
      'ElektroG WEEE compliance',
      'High quality standards',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'Warehousing', 'EPR Compliance', 'Distribution'],
    hasOffice: false,
    isPartner: true,
  },
  GB: {
    name: 'United Kingdom',
    code: 'GB',
    slug: 'united-kingdom',
    capital: 'London',
    population: '67 million',
    region: 'Europe',
    tagline: 'Major economy with post-Brexit trade requirements',
    description: 'The UK remains a major economy and trading partner for global businesses. Post-Brexit, it has established its own customs regime, requiring specific compliance for EU and non-EU trade.',
    metrics: {
      taxRate: '20% VAT',
      dutyRate: 'UK Global Tariff',
      leadTime: '2-4 business days',
      currency: 'GBP (British Pound)',
      language: ['English'],
      timezone: 'GMT/BST (UTC+0/+1)',
    },
    keyIndustries: ['Financial Services', 'Pharmaceuticals', 'Automotive', 'Aerospace', 'Creative Industries'],
    prohibitedItems: ['Narcotics', 'Offensive weapons', 'Counterfeit goods', 'Certain animal products'],
    restrictedItems: ['Pharmaceuticals', 'Food products', 'Chemicals', 'Medical devices', 'Firearms'],
    compliance: [
      {
        title: 'UKCA Marking',
        description: 'UK Conformity Assessed marking required for products sold in Great Britain.',
      },
      {
        title: 'UK REACH',
        description: 'Chemicals must be registered under UK REACH regulations.',
      },
      {
        title: 'EORI Number',
        description: 'Economic Operators Registration and Identification number required for customs.',
      },
    ],
    tradeAgreements: ['UK-EU TCA', 'UK-Japan CEPA', 'UK-Australia FTA', 'UK-New Zealand FTA', 'CPTPP (accession)'],
    painPoints: [
      'Post-Brexit customs requirements',
      'Northern Ireland Protocol complexities',
      'Dual CE/UKCA marking requirements',
      'UK REACH registration',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'UKCA Compliance', 'Warehousing', 'Distribution'],
    hasOffice: false,
    isPartner: true,
  },
  NL: {
    name: 'Netherlands',
    code: 'NL',
    slug: 'netherlands',
    capital: 'Amsterdam',
    population: '17.5 million',
    region: 'Europe',
    tagline: 'Europe\'s gateway with world-class logistics',
    description: 'The Netherlands, with the Port of Rotterdam and Schiphol Airport, serves as Europe\'s logistics gateway. Its business-friendly environment and strategic location make it a top choice for European distribution.',
    metrics: {
      taxRate: '21% VAT',
      dutyRate: 'EU Common Customs Tariff',
      leadTime: '2-3 business days',
      currency: 'EUR (Euro)',
      language: ['Dutch', 'English'],
      timezone: 'CET (UTC+1)',
    },
    keyIndustries: ['Logistics', 'Agriculture', 'Chemicals', 'Technology', 'Financial Services'],
    prohibitedItems: ['Narcotics (except licensed)', 'Counterfeit goods', 'Weapons', 'Certain animal products'],
    restrictedItems: ['Pharmaceuticals', 'Food products', 'Chemicals', 'Medical devices', 'Electronics'],
    compliance: [
      {
        title: 'CE Marking',
        description: 'Mandatory CE marking for regulated product categories.',
      },
      {
        title: 'NVWA Requirements',
        description: 'Netherlands Food and Consumer Product Safety Authority requirements for food/products.',
      },
      {
        title: 'Dutch Packaging Decree',
        description: 'Packaging must comply with Dutch environmental regulations.',
      },
    ],
    tradeAgreements: ['EU Single Market', 'EU-UK TCA', 'EU-Canada CETA', 'EU-Japan EPA', 'EU-Singapore FTA'],
    painPoints: [
      'High logistics costs',
      'Environmental regulations',
      'Packaging compliance',
      'Dutch language for some documents',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'European Distribution', 'Warehousing', 'VAT Fiscal Representation'],
    hasOffice: false,
    isPartner: true,
  },
  FR: {
    name: 'France',
    code: 'FR',
    slug: 'france',
    capital: 'Paris',
    population: '68 million',
    region: 'Europe',
    tagline: 'Major EU economy with sophisticated consumer market',
    description: 'France is one of the EU\'s largest economies with strong sectors in aerospace, luxury goods, and agriculture. Its discerning consumers and strict regulations require careful market entry strategies.',
    metrics: {
      taxRate: '20% VAT',
      dutyRate: 'EU Common Customs Tariff',
      leadTime: '2-4 business days',
      currency: 'EUR (Euro)',
      language: ['French'],
      timezone: 'CET (UTC+1)',
    },
    keyIndustries: ['Aerospace', 'Luxury Goods', 'Automotive', 'Agriculture', 'Tourism'],
    prohibitedItems: ['Narcotics', 'Counterfeit goods', 'Weapons', 'Certain food products'],
    restrictedItems: ['Pharmaceuticals', 'Cosmetics', 'Food products', 'Electronics', 'Chemicals'],
    compliance: [
      {
        title: 'CE Marking',
        description: 'Mandatory CE marking for regulated product categories.',
      },
      {
        title: 'French Language',
        description: 'Product labeling and instructions must be in French.',
      },
      {
        title: 'ANSM Registration',
        description: 'Pharmaceuticals require Agence nationale de sécurité du médicament approval.',
      },
    ],
    tradeAgreements: ['EU Single Market', 'EU-UK TCA', 'EU-Canada CETA', 'EU-Japan EPA', 'EU-Korea FTA'],
    painPoints: [
      'Strict French language requirements',
      'Complex labor regulations',
      'Product certification requirements',
      'Administrative bureaucracy',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'French Language Compliance', 'Warehousing', 'Distribution'],
    hasOffice: false,
    isPartner: true,
  },

  // === NORTH AMERICA ===
  US: {
    name: 'United States',
    code: 'US',
    slug: 'united-states',
    capital: 'Washington, D.C.',
    population: '335 million',
    region: 'North America',
    tagline: 'World\'s largest consumer market',
    description: 'The United States is the world\'s largest consumer market with diverse regulatory requirements across federal and state levels. Success requires understanding complex customs procedures and product compliance.',
    metrics: {
      taxRate: 'Sales Tax (varies by state)',
      dutyRate: '0-37.5% (varies)',
      leadTime: '3-7 business days',
      currency: 'USD (US Dollar)',
      language: ['English'],
      timezone: 'Multiple (EST/CST/MST/PST)',
    },
    keyIndustries: ['Technology', 'Healthcare', 'Financial Services', 'Manufacturing', 'Entertainment'],
    prohibitedItems: ['Narcotics', 'Counterfeit goods', 'Certain agricultural products', 'Sanctioned country goods'],
    restrictedItems: ['Pharmaceuticals', 'Food products', 'Electronics', 'Chemicals', 'Medical devices'],
    compliance: [
      {
        title: 'FDA Registration',
        description: 'Food, drugs, cosmetics, and medical devices require FDA compliance.',
      },
      {
        title: 'FCC Certification',
        description: 'Electronic devices must comply with FCC regulations.',
      },
      {
        title: 'CPSC Compliance',
        description: 'Consumer products must meet Consumer Product Safety Commission requirements.',
      },
      {
        title: 'CBP Requirements',
        description: 'Customs and Border Protection has specific entry and documentation requirements.',
      },
    ],
    tradeAgreements: ['USMCA', 'US-Korea FTA', 'US-Australia FTA', 'US-Israel FTA', 'US-Singapore FTA'],
    painPoints: [
      'Complex multi-agency regulations',
      'State-level compliance variations',
      'Section 301/201 tariffs',
      'ISF (10+2) requirements',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'FDA Compliance', 'FCC Certification Support', 'Warehousing'],
    hasOffice: false,
    isPartner: true,
  },
  CA: {
    name: 'Canada',
    code: 'CA',
    slug: 'canada',
    capital: 'Ottawa',
    population: '40 million',
    region: 'North America',
    tagline: 'Stable market with strong trade ties',
    description: 'Canada offers a stable business environment with strong trade ties to the US and global markets. Bilingual requirements and provincial regulations add complexity to compliance.',
    metrics: {
      taxRate: '5% GST + Provincial Tax',
      dutyRate: '0-35% (varies)',
      leadTime: '3-5 business days',
      currency: 'CAD (Canadian Dollar)',
      language: ['English', 'French'],
      timezone: 'Multiple (PST/MST/CST/EST/AST)',
    },
    keyIndustries: ['Natural Resources', 'Automotive', 'Technology', 'Financial Services', 'Agriculture'],
    prohibitedItems: ['Narcotics', 'Counterfeit goods', 'Hate propaganda', 'Certain weapons'],
    restrictedItems: ['Pharmaceuticals', 'Food products', 'Electronics', 'Chemicals', 'Medical devices'],
    compliance: [
      {
        title: 'Health Canada',
        description: 'Pharmaceuticals, medical devices, and health products require Health Canada approval.',
      },
      {
        title: 'CFIA Requirements',
        description: 'Canadian Food Inspection Agency regulates food, plants, and animals.',
      },
      {
        title: 'Bilingual Labeling',
        description: 'Consumer products must have English and French labeling.',
      },
      {
        title: 'ISED Certification',
        description: 'Electronics require Innovation, Science and Economic Development Canada certification.',
      },
    ],
    tradeAgreements: ['USMCA', 'CETA (EU)', 'CPTPP', 'Canada-Korea FTA', 'Canada-Ukraine FTA'],
    painPoints: [
      'Bilingual labeling requirements',
      'Provincial tax variations',
      'CFIA import permits',
      'Winter logistics challenges',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'Bilingual Compliance', 'Warehousing', 'Cross-Border Solutions'],
    hasOffice: false,
    isPartner: true,
  },
  MX: {
    name: 'Mexico',
    code: 'MX',
    slug: 'mexico',
    capital: 'Mexico City',
    population: '130 million',
    region: 'North America',
    tagline: 'Major manufacturing hub and gateway to Latin America',
    description: 'Mexico has become a major manufacturing hub with extensive trade ties through USMCA. Its strategic location and growing middle class make it an attractive market and nearshoring destination.',
    metrics: {
      taxRate: '16% IVA (VAT)',
      dutyRate: '0-35% (varies)',
      leadTime: '3-7 business days',
      currency: 'MXN (Mexican Peso)',
      language: ['Spanish'],
      timezone: 'Multiple (PST/MST/CST)',
    },
    keyIndustries: ['Automotive', 'Aerospace', 'Electronics', 'Medical Devices', 'Agriculture'],
    prohibitedItems: ['Narcotics', 'Weapons', 'Counterfeit goods', 'Certain agricultural products'],
    restrictedItems: ['Pharmaceuticals', 'Food products', 'Electronics', 'Chemicals', 'Medical devices'],
    compliance: [
      {
        title: 'NOM Standards',
        description: 'Official Mexican Standards (NOMs) certification required for many products.',
      },
      {
        title: 'COFEPRIS',
        description: 'Federal Commission for Protection against Health Risks regulates health products.',
      },
      {
        title: 'IFETEL',
        description: 'Telecommunications equipment requires Federal Telecommunications Institute approval.',
      },
    ],
    tradeAgreements: ['USMCA', 'Mexico-EU FTA', 'Pacific Alliance', 'CPTPP', 'Mexico-Japan EPA'],
    painPoints: [
      'NOM certification requirements',
      'Spanish language documentation',
      'Security considerations',
      'Customs broker requirements',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'NOM Certification Support', 'Warehousing', 'Cross-Border Solutions'],
    hasOffice: false,
    isPartner: true,
  },

  // === SOUTH AMERICA ===
  BR: {
    name: 'Brazil',
    code: 'BR',
    slug: 'brazil',
    capital: 'Brasília',
    population: '215 million',
    region: 'South America',
    tagline: 'Latin America\'s largest economy with vast opportunities',
    description: 'Brazil is Latin America\'s largest economy with a massive consumer market. However, its complex tax system, import procedures, and regulatory requirements demand specialized expertise.',
    metrics: {
      taxRate: 'Multiple (ICMS, IPI, PIS, COFINS)',
      dutyRate: '0-35% + Additional Taxes',
      leadTime: '10-20 business days',
      currency: 'BRL (Brazilian Real)',
      language: ['Portuguese'],
      timezone: 'BRT (UTC-3)',
    },
    keyIndustries: ['Agriculture', 'Mining', 'Automotive', 'Aerospace', 'Oil & Gas'],
    prohibitedItems: ['Narcotics', 'Counterfeit goods', 'Weapons', 'Used consumer goods'],
    restrictedItems: ['Pharmaceuticals', 'Food products', 'Electronics', 'Chemicals', 'Medical devices'],
    compliance: [
      {
        title: 'INMETRO Certification',
        description: 'National Institute of Metrology certification for regulated products.',
      },
      {
        title: 'ANVISA Registration',
        description: 'Health surveillance agency registration for health-related products.',
      },
      {
        title: 'ANATEL Certification',
        description: 'National Telecommunications Agency certification for electronics.',
      },
      {
        title: 'Siscomex',
        description: 'Integrated Foreign Trade System registration required for all imports.',
      },
    ],
    tradeAgreements: ['Mercosur', 'Brazil-India PTA', 'Mercosur-EU FTA (pending)', 'GSTP'],
    painPoints: [
      'Extremely complex tax system ("Custo Brasil")',
      'Lengthy customs clearance',
      'Multiple certification requirements',
      'Portuguese language requirements',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'INMETRO Support', 'Warehousing', 'Distribution'],
    hasOffice: false,
    isPartner: true,
  },
  CL: {
    name: 'Chile',
    code: 'CL',
    slug: 'chile',
    capital: 'Santiago',
    population: '19 million',
    region: 'South America',
    tagline: 'South America\'s most open economy',
    description: 'Chile is known for its economic stability, transparency, and extensive network of free trade agreements. It\'s often considered the easiest Latin American market for doing business.',
    metrics: {
      taxRate: '19% IVA',
      dutyRate: '6% Flat Rate',
      leadTime: '5-10 business days',
      currency: 'CLP (Chilean Peso)',
      language: ['Spanish'],
      timezone: 'CLT (UTC-4)',
    },
    keyIndustries: ['Mining (Copper)', 'Agriculture', 'Seafood', 'Wine', 'Forestry'],
    prohibitedItems: ['Narcotics', 'Weapons', 'Pornographic materials', 'Counterfeit goods'],
    restrictedItems: ['Pharmaceuticals', 'Food products', 'Chemicals', 'Medical devices', 'Pesticides'],
    compliance: [
      {
        title: 'ISP Registration',
        description: 'Public Health Institute registration for pharmaceuticals and medical devices.',
      },
      {
        title: 'SAG Requirements',
        description: 'Agricultural and Livestock Service permits for food and agricultural products.',
      },
      {
        title: 'SEC Certification',
        description: 'Superintendency of Electricity and Fuels certification for electrical products.',
      },
    ],
    tradeAgreements: ['Pacific Alliance', 'CPTPP', 'Chile-US FTA', 'Chile-EU FTA', 'Chile-China FTA'],
    painPoints: [
      'SAG phytosanitary requirements',
      'Spanish documentation',
      'Geographic logistics challenges',
      'Seasonal agricultural restrictions',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'Warehousing', 'Mining Logistics', 'Distribution'],
    hasOffice: false,
    isPartner: true,
  },

  // === AFRICA ===
  ZA: {
    name: 'South Africa',
    code: 'ZA',
    slug: 'south-africa',
    capital: 'Pretoria',
    population: '60 million',
    region: 'Africa',
    tagline: 'Gateway to Sub-Saharan Africa',
    description: 'South Africa is the most developed economy in Africa and serves as a gateway to the continent. Its well-established infrastructure and financial systems make it an ideal base for African expansion.',
    metrics: {
      taxRate: '15% VAT',
      dutyRate: '0-45% (varies)',
      leadTime: '5-10 business days',
      currency: 'ZAR (South African Rand)',
      language: ['English', 'Afrikaans', '9 other official languages'],
      timezone: 'SAST (UTC+2)',
    },
    keyIndustries: ['Mining', 'Automotive', 'Financial Services', 'Agriculture', 'Tourism'],
    prohibitedItems: ['Narcotics', 'Weapons', 'Counterfeit goods', 'Hazardous waste'],
    restrictedItems: ['Pharmaceuticals', 'Food products', 'Electronics', 'Chemicals', 'Second-hand goods'],
    compliance: [
      {
        title: 'SABS Standards',
        description: 'South African Bureau of Standards certification for regulated products.',
      },
      {
        title: 'SAHPRA Registration',
        description: 'South African Health Products Regulatory Authority for health products.',
      },
      {
        title: 'ICASA',
        description: 'Independent Communications Authority certification for telecommunications equipment.',
      },
      {
        title: 'NRCS Approval',
        description: 'National Regulator for Compulsory Specifications for certain products.',
      },
    ],
    tradeAgreements: ['AfCFTA', 'SACU', 'SADC', 'South Africa-EU TDCA', 'AGOA (US)'],
    painPoints: [
      'LOA (Letter of Authority) requirements',
      'Port delays (especially Durban)',
      'BEE compliance considerations',
      'Load shedding affecting logistics',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'African Distribution', 'Warehousing', 'Last-Mile Delivery'],
    hasOffice: false,
    isPartner: false,
  },
  NG: {
    name: 'Nigeria',
    code: 'NG',
    slug: 'nigeria',
    capital: 'Abuja',
    population: '220 million',
    region: 'Africa',
    tagline: 'Africa\'s largest economy and most populous nation',
    description: 'Nigeria is Africa\'s largest economy and most populous country, offering immense market potential. Its complex regulatory environment and infrastructure challenges require experienced partners.',
    metrics: {
      taxRate: '7.5% VAT',
      dutyRate: '5-35% (varies)',
      leadTime: '10-15 business days',
      currency: 'NGN (Nigerian Naira)',
      language: ['English'],
      timezone: 'WAT (UTC+1)',
    },
    keyIndustries: ['Oil & Gas', 'Agriculture', 'Telecommunications', 'Financial Services', 'Entertainment'],
    prohibitedItems: ['Narcotics', 'Weapons', 'Counterfeit currency', 'Used vehicles over 15 years'],
    restrictedItems: ['Pharmaceuticals', 'Food products', 'Electronics', 'Chemicals', 'Used goods'],
    compliance: [
      {
        title: 'SONCAP',
        description: 'Standards Organisation of Nigeria Conformity Assessment Programme.',
      },
      {
        title: 'NAFDAC Registration',
        description: 'National Agency for Food and Drug Administration and Control for regulated products.',
      },
      {
        title: 'NCC Type Approval',
        description: 'Nigerian Communications Commission approval for telecommunications equipment.',
      },
    ],
    tradeAgreements: ['AfCFTA', 'ECOWAS', 'Nigeria-EU EPA'],
    painPoints: [
      'Port congestion (Apapa, Tin Can)',
      'SONCAP requirements',
      'Foreign exchange restrictions',
      'Infrastructure limitations',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'SONCAP Support', 'Warehousing', 'Distribution'],
    hasOffice: false,
    isPartner: true,
  },
  KE: {
    name: 'Kenya',
    code: 'KE',
    slug: 'kenya',
    capital: 'Nairobi',
    population: '55 million',
    region: 'Africa',
    tagline: 'East Africa\'s economic hub',
    description: 'Kenya is East Africa\'s largest economy and a regional hub for trade and finance. Its relatively stable business environment and strategic location make it a gateway to the East African Community.',
    metrics: {
      taxRate: '16% VAT',
      dutyRate: 'EAC Common External Tariff',
      leadTime: '7-10 business days',
      currency: 'KES (Kenyan Shilling)',
      language: ['English', 'Swahili'],
      timezone: 'EAT (UTC+3)',
    },
    keyIndustries: ['Agriculture', 'Tourism', 'Financial Services', 'Technology', 'Manufacturing'],
    prohibitedItems: ['Narcotics', 'Counterfeit goods', 'Pornographic materials', 'Plastic bags'],
    restrictedItems: ['Pharmaceuticals', 'Food products', 'Electronics', 'Chemicals', 'Second-hand goods'],
    compliance: [
      {
        title: 'KEBS Standards',
        description: 'Kenya Bureau of Standards certification for imported products.',
      },
      {
        title: 'PPB Registration',
        description: 'Pharmacy and Poisons Board registration for pharmaceuticals.',
      },
      {
        title: 'PVoC',
        description: 'Pre-Export Verification of Conformity required for certain products.',
      },
    ],
    tradeAgreements: ['AfCFTA', 'EAC', 'COMESA', 'Kenya-UK EPA', 'AGOA (US)'],
    painPoints: [
      'PVoC inspection requirements',
      'Port delays at Mombasa',
      'Documentation requirements',
      'Inter-EAC transit complexities',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'PVoC Support', 'Warehousing', 'EAC Distribution'],
    hasOffice: false,
    isPartner: true,
  },
  EG: {
    name: 'Egypt',
    code: 'EG',
    slug: 'egypt',
    capital: 'Cairo',
    population: '105 million',
    region: 'Africa',
    tagline: 'Strategic gateway connecting Africa, Middle East, and Europe',
    description: 'Egypt\'s strategic location at the crossroads of three continents, combined with the Suez Canal, makes it a critical logistics hub. Its large population offers significant consumer market potential.',
    metrics: {
      taxRate: '14% VAT',
      dutyRate: '5-60% (varies)',
      leadTime: '7-14 business days',
      currency: 'EGP (Egyptian Pound)',
      language: ['Arabic'],
      timezone: 'EET (UTC+2)',
    },
    keyIndustries: ['Oil & Gas', 'Tourism', 'Agriculture', 'Textiles', 'Manufacturing'],
    prohibitedItems: ['Narcotics', 'Weapons', 'Materials offensive to Islam', 'Certain used goods'],
    restrictedItems: ['Pharmaceuticals', 'Food products', 'Electronics', 'Chemicals', 'Medical devices'],
    compliance: [
      {
        title: 'GOEIC Standards',
        description: 'General Organization for Export and Import Control requirements.',
      },
      {
        title: 'EDA Registration',
        description: 'Egyptian Drug Authority registration for pharmaceuticals.',
      },
      {
        title: 'NTRA Approval',
        description: 'National Telecom Regulatory Authority approval for telecommunications equipment.',
      },
    ],
    tradeAgreements: ['AfCFTA', 'COMESA', 'Egypt-EU Association', 'GAFTA', 'QIZ (US)'],
    painPoints: [
      'Arabic documentation requirements',
      'Changing import regulations',
      'Foreign exchange restrictions',
      'Port congestion',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'Warehousing', 'Suez Canal Logistics', 'Distribution'],
    hasOffice: false,
    isPartner: true,
  },

  // === OCEANIA ===
  AU: {
    name: 'Australia',
    code: 'AU',
    slug: 'australia',
    capital: 'Canberra',
    population: '26 million',
    region: 'Oceania',
    tagline: 'Stable economy with strict biosecurity',
    description: 'Australia is a stable, prosperous market with high consumer purchasing power. Its strict biosecurity and product safety regulations require careful compliance, especially for food and agricultural products.',
    metrics: {
      taxRate: '10% GST',
      dutyRate: '0-5% (varies)',
      leadTime: '5-10 business days',
      currency: 'AUD (Australian Dollar)',
      language: ['English'],
      timezone: 'Multiple (AWST/ACST/AEST)',
    },
    keyIndustries: ['Mining', 'Agriculture', 'Financial Services', 'Education', 'Tourism'],
    prohibitedItems: ['Narcotics', 'Weapons', 'Certain animal products', 'Asbestos'],
    restrictedItems: ['Food products', 'Pharmaceuticals', 'Chemicals', 'Electronics', 'Plant materials'],
    compliance: [
      {
        title: 'Biosecurity',
        description: 'Department of Agriculture strict biosecurity requirements for food and biological materials.',
      },
      {
        title: 'TGA Registration',
        description: 'Therapeutic Goods Administration registration for medicines and medical devices.',
      },
      {
        title: 'ACMA Compliance',
        description: 'Australian Communications and Media Authority standards for electronics.',
      },
      {
        title: 'Australian Standards',
        description: 'Products must comply with relevant Australian Standards (AS).',
      },
    ],
    tradeAgreements: ['RCEP', 'CPTPP', 'AUSFTA', 'Australia-China FTA', 'AANZFTA'],
    painPoints: [
      'Strict biosecurity inspections',
      'Distance from major markets',
      'State-level variations',
      'High minimum wages affecting costs',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'Biosecurity Compliance', 'Warehousing', 'Distribution'],
    hasOffice: false,
    isPartner: true,
  },
  NZ: {
    name: 'New Zealand',
    code: 'NZ',
    slug: 'new-zealand',
    capital: 'Wellington',
    population: '5 million',
    region: 'Oceania',
    tagline: 'Clean, green market with high standards',
    description: 'New Zealand is known for its high environmental and product standards. While small, it\'s a sophisticated market with strong agricultural and technology sectors.',
    metrics: {
      taxRate: '15% GST',
      dutyRate: '0-10% (varies)',
      leadTime: '5-10 business days',
      currency: 'NZD (New Zealand Dollar)',
      language: ['English', 'Māori'],
      timezone: 'NZST (UTC+12)',
    },
    keyIndustries: ['Agriculture', 'Tourism', 'Film Production', 'Technology', 'Forestry'],
    prohibitedItems: ['Narcotics', 'Weapons', 'Certain animal products', 'Asbestos'],
    restrictedItems: ['Food products', 'Pharmaceuticals', 'Biosecurity risk items', 'Chemicals'],
    compliance: [
      {
        title: 'MPI Biosecurity',
        description: 'Ministry for Primary Industries biosecurity requirements.',
      },
      {
        title: 'Medsafe',
        description: 'New Zealand Medicines and Medical Devices Safety Authority registration.',
      },
      {
        title: 'Product Safety',
        description: 'Products must meet New Zealand safety standards.',
      },
    ],
    tradeAgreements: ['RCEP', 'CPTPP', 'AANZFTA', 'NZ-China FTA', 'NZ-UK FTA'],
    painPoints: [
      'Strict biosecurity requirements',
      'Small market size',
      'Geographic isolation',
      'High shipping costs',
    ],
    services: ['IOR Services', 'Customs Brokerage', 'Biosecurity Compliance', 'Warehousing', 'Distribution'],
    hasOffice: false,
    isPartner: true,
  },
};

// Get all country codes
export const getAllCountryCodes = (): string[] => Object.keys(COUNTRY_DATA);

// Get country by code
export const getCountryByCode = (code: string): CountryData | undefined => COUNTRY_DATA[code.toUpperCase()];

// Get country by slug
export const getCountryBySlug = (slug: string): CountryData | undefined => {
  return Object.values(COUNTRY_DATA).find(country => country.slug === slug.toLowerCase());
};

// Get countries by region
export const getCountriesByRegion = (region: string): CountryData[] => {
  return Object.values(COUNTRY_DATA).filter(country => country.region.toLowerCase() === region.toLowerCase());
};
